/**
 * algolia-search-modal.js
 *
 * Makes the Algolia AI Search & Discovery autocomplete panel clickable inside
 * the theme's search modal.
 *
 * Why this is needed:
 *   The storefront search opens as a <dialog> via dialog.showModal(), which
 *   places it in the browser's top layer. Algolia appends its results panel
 *   (.aa-Panel) to <body>, OUTSIDE the dialog, so it renders BELOW the modal's
 *   top layer regardless of z-index. Result: clicks on Algolia results are
 *   intercepted by the dialog and never reach the result links.
 *
 *   Instead of changing how the dialog is opened (showModal vs show) — which is
 *   shared by every dialog in the theme and would break the modal's centering,
 *   backdrop and focus trap — we relocate Algolia's panel into the dialog so it
 *   lives in the same top layer. CSS (in snippets/search-modal.liquid) forces it
 *   into normal flow so Algolia's body-relative coordinates are ignored.
 *
 * Scope & side effects:
 *   Self-contained IIFE, no globals. The only DOM it touches is Algolia's own
 *   panel/overlay nodes, which it moves into the #search-modal dialog.
 *   On phones (≤749px) it also keeps the shopper on the panel: «Show more»
 *   loads more rows in place, and the keyboard's «search» key only hides the
 *   keyboard instead of leaving for /search (submit is cancelled).
 */
(function () {
  // Приложение Algolia инициализируется на DOMContentLoaded и монтирует
  // автокомплит в ПЕРВУЮ форму по config.input_selector (дефолт —
  // form[action="/search"]). С появлением мобильного меню (PR #29) первой
  // такой формой в DOM стала GET-форма меню в хедере: автокомплит уезжал
  // в неё, а форма модалки оставалась без подсказок — «поиск на мобиле
  // не работает» (инцидент 2026-07-28). Пин селектора к форме модалки
  // решает и это, и старую проблему языковых URL (/th/search и т.п.):
  // селектор больше не зависит от action, поэтому dynamic_selector
  // (прежний костыль под локали) выключаем.
  // Этот файл подключён с defer: выполняется ПОСЛЕ инлайн-конфига
  // приложения (конец body) и ДО DOMContentLoaded — ровно то окно, когда
  // config уже существует, а init ещё не стартовал.
  if (window.algoliaShopify && window.algoliaShopify.config) {
    window.algoliaShopify.config.dynamic_selector = false;
    window.algoliaShopify.config.input_selector = '#search-modal form[role="search"]';
  }

  var PANEL_SELECTOR = '.aa-Panel, .aa-DetachedOverlay';

  // Порог мобильного скина. Обязан совпадать с медиа-запросом в
  // snippets/pz-search-screen.liquid: скрипт вставляет узлы, размеры им
  // задаёт тот файл — рассинхрон двух порогов и есть механизм регрессии,
  // которую этот файл чинит.
  var MOBILE_MEDIA_QUERY = '(max-width: 749px)';

  // Технический хвост товара («( Standard / GLOBAL )») приложение кладёт
  // ВНУТРЬ .aa-ItemContentTitle отдельным span.algolia-variant. Заголовок
  // обрезан двумя строками, поэтому на длинных названиях хвост исчезал
  // целиком — проверено замером на проде: узел есть, высота 0.
  // Переносим span на уровень выше, соседом заголовка: тогда он становится
  // самостоятельной мета-строкой и не зависит от обрезки названия.
  // ИКОНКА ПЛАТФОРМЫ В СТРОКЕ ВЫДАЧИ.
  // На странице /search её рисует шаблонный хук приложения, а в модалке
  // хука шаблона нет — строку мы только красим. Поэтому платформу берём
  // из текста названия («… - PC (Steam)», «… - Xbox Series X|S»): ровно
  // тот же приём, что уже работает фолбэком в algolia_custom_hooks.js.
  function platformPath(title) {
    var paths = window.PZ_PLATFORM_PATHS;
    if (!paths) return null;

    var text = String(title || '').toLowerCase();
    if (text.indexOf('xbox') > -1) return paths.xbox;
    if (text.indexOf('nintendo') > -1 || text.indexOf('switch') > -1) return paths.nintendo;
    if (text.indexOf('playstation') > -1 || text.indexOf('psn') > -1) return paths.ps;
    // Steam (он же у всех PC) в выдаче не показываем — Евгений 2026-09-22:
    // платформа и так в названии; значок Steam — только на странице товара
    return null;
  }

  function addPlatformBadges(root) {
    var items = root.querySelectorAll('.aa-ProductItem');

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var picture = item.querySelector('.aa-ItemPicture');
      var title = item.querySelector('.aa-ItemContentTitle');
      if (!picture || !title || picture.querySelector('.pz-search-plat')) continue;

      var path = platformPath(title.textContent);
      if (!path) continue;

      var badge = document.createElement('span');
      badge.className = 'pz-search-plat';
      badge.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + path + '"></path></svg>';
      picture.appendChild(badge);
    }
  }

  function detachVariantLines(root) {
    var titles = root.querySelectorAll('.aa-ItemContentTitle .algolia-variant');

    for (var i = 0; i < titles.length; i++) {
      var variant = titles[i];
      var body = variant.closest('.aa-ItemContentBody');
      if (body) body.appendChild(variant);
    }
  }

  // ── Кнопка «See all N results» (хендоф Search Mobile, экран 2) ──────
  // Внизу панели подсказок — переход на страницу результатов. Приложение
  // рендерит .aa-SourceFooter ПУСТЫМ (проверено на проде 2026-08-01) —
  // это и есть точка монтирования, шаблоны приложения не трогаем.
  //
  // Число результатов берём из transform-хука приложения: его второй
  // аргумент — массив ответов Algolia, [0].nbHits/query (сигнатура снята
  // живым зондом 2026-08-01: для «dark souls» nbHits=81). Хук срабатывает
  // до отрисовки панели, так что к моменту рендера счётчик уже свежий.
  var lastSearch = { query: '', total: 0 };

  (function captureResultCount() {
    var api = window.algoliaShopify && window.algoliaShopify.hooks;
    if (!api || typeof api.registerHook !== 'function') {
      document.addEventListener('algolia.hooks.initialize',
        captureResultCount, { once: true });
      return;
    }
    api.registerHook('beforeAutocompleteProductTransformResponseHits',
      function (hits, results) {
        var first = results && results[0];
        if (first) {
          lastSearch.query = first.query || '';
          lastSearch.total = first.nbHits || 0;
        }
        return hits;
      });
  })();

  // ── «Показать ещё» прямо в панели (решение Евгения 2026-08-01:
  // на мобильном не уводить с экрана — просто показать больше игр) ──────
  //
  // Догрузка идёт мимо приложения: тем же запросом в Algolia с offset/
  // length (окно продолжает ТОЧНО тот же порядок выдачи), с тем же
  // регион-правилом (window.pzRegionFilter из pz-region-filter.js).
  // Новые строки — КЛОНЫ живой строки приложения с подменой полей:
  // разметку и стили не реплицируем, совместимость один в один — включая
  // формат цены (приложение печатает сырую цену индекса со значком
  // активной валюты; конверсии там нет — известный баг подсказок, наши
  // строки ему бит-в-бит соответствуют, чтобы колонка была однородной).
  var EXTRA_ROWS_PER_TAP = 10;

  function imageWithSizeSuffix(url, size) {
    if (typeof url !== 'string' || !url) return url;
    var parts = url.split('?');
    var sized = parts[0].replace(/\.(\w{2,4})$/, '_' + size + 'x.$1');
    return parts[1] ? sized + '?' + parts[1] : sized;
  }

  function fillClonedRow(row, hit) {
    var link = row.querySelector('a.aa-ProductItem, a.aa-ItemLink');
    if (link) {
      link.setAttribute('href',
        '/products/' + hit.handle + '?variant=' + hit.objectID);
    }
    var img = row.querySelector('img');
    if (img) {
      img.src = imageWithSizeSuffix(hit.image, 125);
      img.srcset = [125, 250, 375].map(function (size, index) {
        return imageWithSizeSuffix(hit.image, size) + ' ' + (index + 1) + 'x';
      }).join(', ');
      img.alt = hit.title || '';
    }
    var title = row.querySelector('.aa-ItemContentTitle');
    if (title) title.textContent = hit.title || '';
    var variant = row.querySelector('.algolia-variant');
    if (variant) {
      variant.textContent = hit.variant_title
        ? '( ' + hit.variant_title + ' )' : '';
    }
    var vendor = row.querySelector('.algolia-vendor');
    if (vendor) vendor.textContent = hit.vendor ? 'Game by ' + hit.vendor : '';
    // цена: тот же текстовый шаблон, что у строки-донора (число + значок)
    var priceNode = null;
    row.querySelectorAll('span, div').forEach(function (node) {
      if (!node.firstElementChild && /\d/.test(node.textContent)
          && node !== title && node !== variant && node !== vendor) {
        priceNode = node;
      }
    });
    if (priceNode) {
      var template = priceNode.textContent;
      var amount = (typeof hit.price === 'number' ? hit.price : 0).toFixed(2);
      priceNode.textContent = template.replace(/[\d.,]+/, amount);
    }
    // бейдж платформы: чужой снять — наблюдатель дорисует по НОВОМУ названию
    var badge = row.querySelector('.pz-search-plat');
    if (badge) badge.remove();
    row.setAttribute('data-pz-extra-row', '1');
    return row;
  }

  function loadMoreIntoPanel(footer, button) {
    var config = window.algoliaShopify && window.algoliaShopify.config;
    var list = footer.closest('.aa-Source')
      && footer.closest('.aa-Source').querySelector('.aa-List');
    var templateRow = list && list.querySelector('.aa-Item');
    if (!config || !list || !templateRow) return;
    var shown = list.querySelectorAll('.aa-Item').length;
    var filters = typeof window.pzRegionFilter === 'function'
      ? window.pzRegionFilter('') : '';
    button.setAttribute('data-pz-busy', '1');
    fetch('https://' + config.app_id + '-dsn.algolia.net/1/indexes/'
        + config.index_prefix + 'products/query', {
      method: 'POST',
      headers: {
        'X-Algolia-API-Key': config.search_api_key,
        'X-Algolia-Application-Id': config.app_id,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: lastSearch.query,
        offset: shown,
        length: EXTRA_ROWS_PER_TAP,
        filters: filters
      })
    }).then(function (response) { return response.json(); })
      .then(function (data) {
        (data.hits || []).forEach(function (hit) {
          list.appendChild(fillClonedRow(templateRow.cloneNode(true), hit));
        });
        lastSearch.total = typeof data.nbHits === 'number'
          ? data.nbHits : lastSearch.total;
      })
      .finally(function () { button.removeAttribute('data-pz-busy'); });
  }

  // 🔴 Функция обязана быть ИДЕМПОТЕНТНОЙ: её зовёт наблюдатель мутаций
  // панели, а она сама панель и мутирует. Безусловное присваивание
  // textContent/href рождает мутацию на КАЖДЫЙ вызов → наблюдатель зовёт
  // снова → бесконечный цикл, main thread занят, ввод в поле мёртв
  // (инцидент 2026-08-01: «вторая буква не вводится, всё зависает»).
  // DOM трогаем только когда значение реально изменилось — цикл гаснет
  // за один проход.
  function renderSeeAllButton(root) {
    var footer = root.querySelector('.aa-SourceFooter');
    if (!footer) return;
    var button = footer.querySelector('.pz-search-see-all');
    var source = footer.closest('.aa-Source');
    var list = source && source.querySelector('.aa-List');
    var shownRows = list ? list.querySelectorAll('.aa-Item').length : 0;
    var allShown = lastSearch.total > 0 && shownRows >= lastSearch.total;
    // Порядок веток держит идемпотентность: после remove() следующая
    // проверка попадает в ту же ветку с button=null и НЕ мутирует —
    // цикл наблюдателя гаснет за один проход.
    if (!lastSearch.query || !lastSearch.total || allShown) {
      if (button) button.remove();
      return;
    }
    if (!button) {
      button = document.createElement('a');
      button.className = 'pz-search-see-all';
      // Мобайл: остаёмся на экране, догружаем в панель (решение Евгения
      // 2026-08-01). Десктоп: обычная навигация на страницу результатов.
      button.addEventListener('click', function (event) {
        if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
        event.preventDefault();
        if (button.getAttribute('data-pz-busy')) return;
        var ownFooter = button.closest('.aa-SourceFooter');
        if (ownFooter) loadMoreIntoPanel(ownFooter, button);
      });
      footer.appendChild(button);
    }
    // href — из ЖИВОГО значения поля, не из хука: повторный запрос
    // приложение отдаёт из кэша БЕЗ вызова transform-хука, и адрес
    // отставал бы на букву (замер: поле «witcher», href «witche»).
    // Счётчику отставание на кэш-попадании простительно, адресу — нет.
    var input = root.closest('dialog')
      ? root.closest('dialog').querySelector('.aa-Input') : null;
    var query = (input && input.value) || lastSearch.query;
    var href = '/search?q=' + encodeURIComponent(query);
    var hasExtras = list && !!list.querySelector('[data-pz-extra-row]');
    var label = hasExtras
      ? 'Show more'
      : 'See all ' + lastSearch.total + ' results';
    if (button.getAttribute('href') !== href) {
      button.setAttribute('href', href);
    }
    if (button.textContent !== label) {
      button.textContent = label;
    }
  }

  // 🔴 ОБЕ ДОВОДКИ ВЫШЕ — ТОЛЬКО МОБИЛЬНЫЕ. Их геометрию задаёт
  // snippets/pz-search-screen.liquid внутри @media (max-width: 749px), и
  // на десктопе они оставались без размеров: span бейджа не имеет своих,
  // а <svg> без width/height растягивается на всю ширину контейнера
  // (viewBox 24×24 держит квадрат) — в десктопной выдаче вырастали
  // гигантские логотипы платформы, а вынесенный из заголовка хвост
  // «( Standard / GLOBAL )» становился отдельной крупной строкой.
  // Инцидент 2026-07-30, подтверждён замером на проде: в задеплоенном
  // файле ноль matchMedia, правила бейджа только в ≤749px.
  // Проверяем в МОМЕНТ ВЫЗОВА, а не один раз при загрузке: поворот
  // экрана и изменение размера окна не должны включать мобильную
  // доводку на десктопе.
  function applyMobileRowLayout(root) {
    if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
    detachVariantLines(root);
    addPlatformBadges(root);
    renderSeeAllButton(root);
  }

  // ── «search» на клавиатуре телефона — остаёмся на экране подсказок ──
  // Решение Евгения 2026-09-22 (PZ-576), продолжение правила «Показать
  // ещё» (2026-08-01): на мобильном с экрана поиска не уводим. Кнопка
  // «search»/«Go» клавиатуры отправляет форму Algolia .aa-Form, и
  // обработчик приложения тут же, синхронно, уводит на /search?q= (замер
  // на проде 2026-09-22: submit на .aa-Form → beforeunload). Слушатель
  // стоит в фазе перехвата на document — срабатывает раньше слушателя
  // самой формы при любом порядке регистрации; stopPropagation, а не
  // stopImmediatePropagation — чужие слушатели document (аналитика) живы.
  // Снимаем фокус: клавиатура прячется, панель остаётся — но только на
  // сенсорном экране: там autocomplete не закрывает панель по blur
  // (проверено тем же замером). В узком окне десктопа с мышью blur закрыл
  // бы панель и Enter никуда бы не вёл — там переход на /search, как был.
  // Пустое поле: событие гасим, фокус не трогаем — показывать под
  // клавиатурой нечего. Поле InstantSearch на самой странице /search не
  // наше — только форма внутри модалки.
  var MOBILE_TOUCH_MEDIA_QUERY = MOBILE_MEDIA_QUERY + ' and (pointer: coarse)';

  function keepPanelOnMobileSubmit(event, modal) {
    if (!window.matchMedia(MOBILE_TOUCH_MEDIA_QUERY).matches) return;
    var form = event.target;
    if (!(form instanceof Element) || !form.matches('.aa-Form')) return;
    if (!modal.contains(form)) return;
    event.preventDefault();
    event.stopPropagation();
    var input = form.querySelector('.aa-Input');
    if (input && input.value.trim()) input.blur();
  }

  function init() {
    var modal = document.getElementById('search-modal');
    if (!modal) return;

    var dialog = modal.querySelector('dialog');
    if (!dialog) return;

    // Prefer placing the panel inside the form (right under the input); fall
    // back to the dialog itself if the form markup ever changes.
    function host() {
      return dialog.querySelector('.predictive-search-form') || dialog;
    }

    function relocate(node) {
      if (!node || node.nodeType !== 1) return;
      if (typeof node.matches !== 'function' || !node.matches(PANEL_SELECTOR)) return;
      applyMobileRowLayout(node);

      // Панель перерисовывается на каждый ввод: без наблюдателя перенос
      // сработал бы только на первой отрисовке.
      new MutationObserver(function () {
        applyMobileRowLayout(node);
      }).observe(node, { childList: true, subtree: true });

      var target = host();
      if (node.parentElement === target) return; // already relocated
      target.appendChild(node);
    }

    // Move any panel that already exists on load.
    document.querySelectorAll(PANEL_SELECTOR).forEach(relocate);

    // Algolia (re)creates the panel on <body> when the user types, and again on
    // each open. Relocate it as soon as it is added.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          relocate(added[j]);
        }
      }
    });
    observer.observe(document.body, { childList: true });

    // ── Клавиатура с ПЕРВОГО тапа: синхронное открытие в жесте ─────────
    // Корень всех итераций: showDialog темы открывает диалог внутри
    // requestAnimationFrame — ПОСЛЕ жеста. Пока диалог закрыт, его инпут
    // нефокусопригоден; фокус после открытия — вне жеста, iOS клавиатуру
    // не поднимает (проверено на устройстве 2026-08-01). Прокси-инпут
    // откачен: его клавиатура запускала системную анимацию, в которую iOS
    // придерживает rAF — открытие опаздывало («нужно два нажатия»).
    //
    // Решение: на pointerup (бежит ДО click при любом порядке и фазах
    // чужих слушателей) прогоняем СОБСТВЕННЫЙ showDialog компонента с
    // requestAnimationFrame, подменённым на немедленный вызов: вся логика
    // компонента — скролл-лок с верным scrollY, приватные поля, событие
    // dialog:open, слушатели закрытия — выполняется как есть, просто
    // синхронно. Диалог открыт ещё в жесте → фокус в НАСТОЯЩЕЕ поле
    // легален, клавиатура поднимается; делегированный click-обработчик
    // темы придёт к уже открытому диалогу и выйдет по гарду if(open) —
    // второго showModal и гонки не существует по построению.
    //
    // ТОЛЬКО мобильный скин: там диалог полноэкранный, и хвостовой click
    // жеста ложится внутрь него. На десктопе диалог — центрированное
    // окно, иконка поиска остаётся снаружи, и хвостовой click пришёлся бы
    // в ::backdrop — слушатель закрытия компонента тут же захлопнул бы
    // окно. Десктопу синхронность и не нужна: клавиатуры нет, фокус
    // ставит dialog:open ниже.
    document.addEventListener('pointerup', function (event) {
      if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
      if (!(event.target instanceof Element)) return;
      var trigger = event.target.closest(
        '[on\\:click*="search-modal"], search-button button');
      if (!trigger || dialog.open) return;
      var nativeRequestAnimationFrame = window.requestAnimationFrame;
      window.requestAnimationFrame = function (callback) {
        callback(performance.now());
        return 0;
      };
      try {
        modal.showDialog();
      } finally {
        window.requestAnimationFrame = nativeRequestAnimationFrame;
      }
      var input = dialog.querySelector(
        '.aa-Input, input[type="search"], input.search-input');
      if (input) input.focus();
    }, true);

    document.addEventListener('submit', function (event) {
      keepPanelOnMobileSubmit(event, modal);
    }, true);

    // Страховка на не-жестовые открытия (deep link, чужой скрипт):
    // фокус при событии открытия; ретраи — на монтирование .aa-Input.
    modal.addEventListener('dialog:open', function () {
      var tries = 0;
      (function focusInput() {
        var input = dialog.querySelector(
          '.aa-Input, input[type="search"], input.search-input');
        if (input) {
          input.focus();
        } else if (tries++ < 10) {
          setTimeout(focusInput, 50);
        }
      })();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
